/* eslint-disable @typescript-eslint/no-explicit-any */

<template>
    <div ref="refGrid" :class="cardCss()"> 
        <grid-header :css-header="cssHeader" @loadgrid="loadGrid" v-model="search" 
                     v-show="headerShown" :loading="loading"
                     :reload-button="reloadButton" :auto-search="autoSearch" :auto-search-timer="autoSearchTimer">
            <grid-title>
                <slot name="title"/>
            </grid-title>
            <slot name="header"/>
            <template #headerright>
                <slot name="headerright"/>
            </template>
        </grid-header>
        <grid-body>
            <Grid
                :style="style"
                :sortable="sortable" :sort="currSort" 
                :scrollable="scrollable"
                :pageable="realPageable"
                :skip="currSkip" :take="currTake"
                :columns="columns"
                @datastatechange="dataStateChange"
                :data-items="dataResult" >
                <grid-norecords>
                    {{ getNoRecordsText() }}
                </grid-norecords>
            </Grid>
        </grid-body>
    </div>
</template>

<style lang="scss"> // deliberately not scoped
$font-size: 12px;
$header-font-size:20px;
    .card-header {
        input,
        select,
        button {
            font-size:$font-size;            
        }
        h4 {
            font-size:$header-font-size;
            font-weight:500;
        }
    }
    .k-grid {
        td, th {
            &.gLeft {
                text-align: left;
            }
            &.gRight {
                text-align: right;
            }
            &.gCenter {
                text-align: center;
            }
        }
    }
</style>

<script lang="ts">

import { h, VNode, defineComponent } from "vue";

import GridHeader from "@/shared/components/common/GridHeader.vue";
import GridTitle from "@/shared/components/common/GridTitle.vue";
import GridBody from "@/shared/components/common/GridBody.vue";

import { Grid, GridNoRecords, GridDataStateChangeEvent, GridSortSettings, GridPagerSettings, GridCellProps, GridColumnProps } from "@progress/kendo-vue-grid";
import { DataResult, State, SortDescriptor, CompositeFilterDescriptor } from "@progress/kendo-data-query";

export function getEmptyDataResult(): DataResult {
    return { data: [], total: 0};
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function customCell(oldH: any, defaultRendering: VNode | null, props: GridCellProps, listeners: any, vueComp: any, parms?: any): VNode {
    let tdProps =  {
        colspan: props.colSpan,
        class: props.className,
        role: "gridcell", "aria-selected": props.isSelected,
        "data-grid-col-index": props.columnIndex,
    };
    if (parms)
        tdProps = Object.assign(tdProps, parms);
    return h(vueComp, tdProps);
}

const GridPanelComponent = defineComponent({
    components: {
        GridHeader, GridTitle, GridBody,
        Grid,
        "grid-norecords": GridNoRecords,
    },
    props: {
        modelValue: { type: Object as () => DataResult, required: true },
        cssBorder: { type: String, required: false, default: "" },
        cssHeader: { type: String, required: false, default: "" },
        noRecordsText: { type: String, required: false, default: "No Records Found" },
        headerShown: { type: Boolean, required: false, default: true },
        autoSearch: { type: Boolean, required: false, default: false },
        autoSearchTimer: { type: Number, required: false, default: 0 },
        reloadButton: { type: Boolean, required: false, default: true },
        style: { type: String, required: false, default: "none" },
        sortable: { type: [Boolean, Object], required: false, default: true },
        sort: { type: Array as () => SortDescriptor[], required: false, default: () => [] },
        filterable: { type: Boolean, required: false, default: false },
        filter: { type: Object as () => CompositeFilterDescriptor, required: false, default: undefined },
        pageable: { type: [Object, Boolean] as unknown as () => GridPagerSettings | boolean, required: false, default: () => ({ buttonCount: 4, pageSizes: [ 10, 25, 50 ] }) },
        skip: { type: Number, required: false, default: 0 },
        take: { type: Number, required: false, default: 10 },
        columns: { type: Array as () => GridColumnProps[], required: true },
        scrollable: { type: String, required: false, default: "scrollable" },
    },
    emits: ["loadgrid"],
    data() {
        return {
            loading: false,
            currSkip: this.skip,
            currTake: this.take,
            currSort: this.sort,
            currFilter: this.filter,
            search: "",
            pagerInterval: 0,
            dataResult: getEmptyDataResult(),
            cachedPageable: false as GridPagerSettings | boolean,
        };
    },
    computed: {
        realPageable(): GridPagerSettings | boolean {
            return this.cachedPageable;
        },
    },
    mounted() {
        this.currSkip = this.skip;
        this.currTake = this.take;
        this.currSort = this.sort;
        this.currFilter = this.filter;
        this.loadGrid();
        this.pagerInterval = setInterval((): void => {
            this.checkVisibility();
        }, 200) as unknown as number;
    },
    unmounted() {
        if (this.pagerInterval) {
            clearInterval(this.pagerInterval);
            this.pagerInterval = 0;
        }
    },
    methods: {
        checkVisibility(): void {
            const refGrid = this.$refs.refGrid as HTMLElement;
            if (!refGrid) return;
            const rect = refGrid.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
                if (!this.cachedPageable)
                    this.cachedPageable = this.pageable; 
            } else {
                if (this.cachedPageable)
                    this.cachedPageable = false; 
            }
        },
        cardCss(): string  {
            return `card ${this.cssBorder}`;
        },
        loadGrid(search?: string): void {
            this.search = search || "";
            this.reload();
        },
        reload(): void {
            this.loading = true;
            this.keepTbodyHeight();
            this.dataResult.data = []; // clear data to show grid's loading indicator

            const dataState: State = {
                skip: this.currSkip,
                take: this.currTake,
                sort: this.currSort,
                filter: this.currFilter,
            };
            this.$emit("loadgrid", dataState, this.search);
        },
        dataStateChange(event: GridDataStateChangeEvent): void {
            this.loading = true;
            this.keepTbodyHeight();
            this.dataResult.data = []; // clear data to show grid's loading indicator

            const dataState: State = event.data;
            this.currSkip = dataState.skip || 0;
            this.currTake = dataState.take || 10;
            this.currSort = dataState.sort || [];
            this.currFilter = dataState.filter;
            this.$emit("loadgrid", dataState, this.search);
        },
        getNoRecordsText(): string {
            if (this.loading) 
                return "Loading...";
            else
                return this.noRecordsText;
        },
        keepTbodyHeight(): void {
            const tbody = this.$el.querySelector("tbody") as HTMLElement;
            const h = tbody.clientHeight;
            tbody.style.height = `${h}px`;
        },
        clearTbodyHeight(): void {
            const tbody = this.$el.querySelector("tbody") as HTMLElement;
            tbody.style.height = "";
        },
    },
    watch: {
        modelValue(val): void { 
            this.dataResult = val;
            this.loading = false; // setting the model automatically clears the loading indicator 
            this.clearTbodyHeight();
        },
        skip(val): void { this.currSkip = val; },
        take(val): void { this.currTake = val; },
    },
});

// Add static properties for backwards compatibility
(GridPanelComponent as any).EmptyDataResult = getEmptyDataResult();
(GridPanelComponent as any).customCell = customCell;

export default GridPanelComponent;

</script>
